// ═══════════════════════════════════════════════════════════════
// ARCHIVO DESTINO: frontend/src/combosMismoPartido.js
// ═══════════════════════════════════════════════════════════════
//
// La combinada del MISMO PARTIDO, para las dos pantallas que la arman:
// `FlujoManual` (Apuesta manual) y `EnVivo`.
//
// Son dos conceptos y por eso son dos archivos: `agenciaPicks.js` resuelve
// QUÉ picks del carrito son de este partido —la forma del carrito—, y acá
// vive lo que eso CUESTA: cuántos entran y a qué cuota.
//
// Por qué un módulo y no una copia en cada pantalla. Hasta el T7 de
// `odd/tasks/sportsbook-homologado.md` esta regla existía una sola vez, en
// `FlujoManual`, y `EnVivo` no la tenía: admitía UNA selección por partido
// porque `toggleBet` sacaba todas las del partido antes de agregar. Cerrar
// esa asimetría copiando el conteo, el tope y el `useEffect` de
// cotización a la segunda pantalla era repetir exactamente la enfermedad
// que ese documento está curando —`Agencia.jsx` forkeó las piezas del
// jugador porque no había nada compartido— y la próxima vez que se mueva
// el margen se movería en una pantalla sola.
//
// El servidor es el que manda, y se verificó antes de apoyarse en esto:
// `/api/betslip` recalcula la cuota y hace cumplir `max_picks` con un 400.
// O sea que nada de acá es la autoridad del precio: es lo que el cajero ve
// mientras arma, y tiene que coincidir con lo que el servidor va a cobrar.
//
// Desde el T8 ese recálculo es POR PARTIDO —`_cotizar_mismo_partido` en
// casino_api.py agrupa por `home`+`away`, cobra el margen de correlación
// solo dentro de cada grupo de dos o más, y mide el tope contra el partido
// más cargado y no contra el boleto—, y es EL MISMO helper que contesta
// `/api/bet-builder/cotizar`. Por eso la cotización de acá manda `home` y
// `away` con cada selección: sin ellos el servidor no puede agrupar y cotiza
// con el margen del boleto entero, que es la cuenta vieja.
//
// OJO con el gateo por agencia: `/api/betslip` NO llama a
// `_bb_activo_para` en ninguna parte. Esa bandera solo la leen
// `/api/agencias/me/features`, `/api/bet-builder/config` y el panel de
// admin. Decide qué puede ARMAR el cajero en pantalla, no qué acepta el
// servidor.
import { useEffect, useState } from "react";
import { getFrontendConfig } from "./config";
import { picksDelPartido } from "./agenciaPicks";

// El mismo `||4` que tenía `FlujoManual`. No es cosmético: la respuesta de
// `/api/agencias/me/features` llega por red y el estado arranca en
// `{bet_builder:false, bb_max_picks:4}`, así que entre el montaje y la
// respuesta —y en cualquier 500— el campo puede no tener valor.
export const TOPE_POR_DEFECTO = 4;

// El tope de selecciones del mismo partido que la agencia tiene
// configurado. `Number(...)` porque la config del admin se guarda como
// TEXTO en la base (`bb_margen_correlacion` es `str(...)` en
// casino_api.py:22974): comparado con `>=` sin convertir, `3 >= "4"` da
// falso y el tope se corre solo.
export function topeMismoPartido(bbFeature) {
  return Number(bbFeature && bbFeature.bb_max_picks) || TOPE_POR_DEFECTO;
}

// La clave de un partido dentro del carrito. Es `home`+`away` y no el id
// del evento por dos razones medidas:
//  · es el único par de campos que las DOS formas de carrito comparten
//    (`FlujoManual` guarda un `id` compuesto con la etiqueta adentro,
//    `EnVivo` guarda el id del evento, compartido entre selecciones);
//  · el listado en vivo se relee cada 30 segundos y puede cambiar el id
//    del evento. Contando por id, el margen de correlación se perdería
//    justo cuando el boleto ya tiene dos mercados del partido.
export function clavePartido(pick) {
  return `${(pick && pick.home) || ""}-${(pick && pick.away) || ""}`;
}

// Si el carrito tiene dos o más selecciones de un mismo partido. Es lo que
// viaja como `mismo_partido` y decide si el servidor aplica el margen de
// correlación o multiplica a secas.
export function hayMismoPartido(picks) {
  const porPartido = {};
  (Array.isArray(picks) ? picks : []).forEach((p) => {
    const clave = clavePartido(p);
    porPartido[clave] = (porPartido[clave] || 0) + 1;
  });
  return Object.values(porPartido).some((c) => c >= 2);
}

// Si todavía entra una selección del partido de `ev`. El tope es POR
// PARTIDO: un partido lleno no puede dejar al cajero sin poder sumar otro
// partido, que es una combinada normal.
export function puedeSumarAlPartido(picks, ev, tope) {
  return picksDelPartido(picks, ev).length < tope;
}

// La cuota sin margen: el producto de las cuotas del carrito. Un carrito
// vacío vale 1 y no 0, porque el retorno que se muestra en el mostrador es
// `monto * cuota`.
export function cuotaBruta(picks) {
  const lista = Array.isArray(picks) ? picks : [];
  if (!lista.length) return 1;
  return lista.reduce((a, p) => a * (Number(p && p.odd) || 1), 1);
}

// Lo que identifica al carrito para saber si hay que volver a cotizar.
// Incluye la etiqueta a propósito: en `EnVivo` dos selecciones del mismo
// partido comparten el `id` —es el del evento—, así que una firma de
// `[id, odd]` sola no las distingue.
// Incluye la clave del partido desde el T8: la cotización ahora depende de
// CÓMO se agrupan las selecciones, así que si `home`/`away` cambian hay que
// volver a preguntar aunque el id y la cuota no se hayan movido.
export function firmaDeCarrito(picks) {
  return JSON.stringify((Array.isArray(picks) ? picks : []).map((p) => [
    p && p.id,
    (p && (p.label != null ? p.label : p.sel)) || "",
    p && p.odd,
    clavePartido(p),
  ]));
}

// La cuota que se le muestra al cajero y que viaja como `odd_total`.
//
// Con mismo partido, `/api/bet-builder/cotizar` devuelve la cuota ya con el
// margen de correlación aplicado (casino_api.py:22910). Es matemática pura
// sobre `{picks, mismo_partido}`: NO tiene noción de vivo ni de prematch,
// y por eso la misma llamada sirve para las dos pantallas.
//
// Mientras el pedido está en vuelo se sigue mostrando la cuota anterior, no
// un parpadeo a bruto: el cajero está leyendo el retorno en voz alta.
export function useCuotaCombinada(picks) {
  const mismoPartido = hayMismoPartido(picks);
  const totBruto = cuotaBruta(picks);
  const firma = firmaDeCarrito(picks);
  const [cuotaProt, setCuotaProt] = useState(null);

  useEffect(() => {
    const lista = Array.isArray(picks) ? picks : [];
    if (lista.length === 0) { setCuotaProt(null); return; }
    let vivo = true;
    // La config se resuelve ACÁ y no al cargar el módulo: así este archivo
    // se puede importar en una prueba sin las siete variables de entorno,
    // que es lo que hace que las funciones puras de arriba se prueben con
    // valores de verdad y no con afirmaciones sobre el texto del archivo.
    const { apiUrl } = getFrontendConfig();
    fetch(`${apiUrl}/api/bet-builder/cotizar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        // `home` y `away` van desde el T8 y NO son decorativos: sin ellos el
        // servidor no puede agrupar por partido y le cobra el margen de
        // correlación al boleto entero, así que la cuota que el cajero lee
        // sería la vieja y la que se cobra la nueva. Mandar solo `{odd}` era
        // tener dos reglas de precio para el mismo boleto.
        picks: lista.map((p) => ({ odd: p.odd, home: p.home, away: p.away })),
        mismo_partido: mismoPartido,
      }),
    }).then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (vivo && d) setCuotaProt(d.cuota); })
      .catch(() => {});
    return () => { vivo = false; };
    // eslint-disable-next-line
  }, [firma, mismoPartido]);

  return {
    mismoPartido,
    totBruto,
    cuotaProt,
    totOdd: cuotaProt != null ? cuotaProt : totBruto,
  };
}
